import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { USER_ROLES } from '@/config/permissions'
import { fetchComplianceAudit } from '@/apis/complianceAudit'
import { useTeamContext } from '@/hooks/useTeamContext'
import { useApiReady } from '@/hooks/useApiReady'
import type { ComplianceAuditResult } from '@/utils/complianceAudit/types'

export const complianceAuditKeys = {
  all: ['complianceAudit'] as const,
  detail: (teamId: string | null | undefined, month: string) =>
    [...complianceAuditKeys.all, teamId, month] as const,
}

export function useComplianceAudit() {
  const { activeTeamId, effectiveRole } = useTeamContext()
  const apiReady = useApiReady()
  const isTeamAdmin = effectiveRole === USER_ROLES.TEAM_ADMIN
  const reportMonth = useMemo(() => dayjs().format('YYYY-MM'), [])

  return useQuery({
    queryKey: complianceAuditKeys.detail(activeTeamId, reportMonth),
    queryFn: async (): Promise<ComplianceAuditResult> => {
      const res = await fetchComplianceAudit(reportMonth, activeTeamId)
      if (res.code === 200 && res.data) {
        return res.data
      }
      throw new Error(res.message || 'Failed to fetch compliance audit')
    },
    enabled: apiReady && !!activeTeamId && isTeamAdmin,
  })
}
